/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Menu, MenuItem, useTheme } from '@mui/material';
import type { SxProps } from '@mui/material';
import { isEqual, isString } from 'lodash';
import React from 'react';
import type { MosaicBranch, MosaicNode } from 'react-mosaic-component';
import { MosaicContext } from 'react-mosaic-component';
import { useSelector } from 'react-redux';
import { OverlayPluginMenuButton } from '@opengeoweb/plugins';
import {
  CustomIconButton,
  sessionStorageProvider,
  useConfirmationDialog,
} from '@opengeoweb/shared';
import { Expand, UnExpand, Close, ChevronDown } from '@opengeoweb/theme';
import LinkMenuConnect from './LinkMenu/LinkMenuConnect';
import { SplitButtonsConnect } from './SplitButtonsConnect';
import type { AppStore } from '../../store/types';
import { getMosaicNode } from '../../store/workspace/selectors';
import { useWorkspaceTranslation } from '../../utils/i18n';

const buttonStyle: SxProps = {
  marginLeft: 0.5,
  marginRight: 0.5,
};

interface WorkspaceControlsProps {
  panelId: string;
  path: MosaicBranch[];
  createNode?: () => MosaicNode<string>;
  shouldPreventCloseView?: boolean;
  removeLinkedMap: (panelId: string) => void;
}

const WorkspaceControls: React.FC<WorkspaceControlsProps> = ({
  panelId,
  createNode,
  path,
  shouldPreventCloseView = false,
  removeLinkedMap,
}: WorkspaceControlsProps) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const confirmDialog = useConfirmationDialog();
  const { mosaicActions: actions } = React.useContext(MosaicContext);
  const { t } = useWorkspaceTranslation();

  const config = sessionStorageProvider.getConfig();
  const hasPluginsConfigured = Boolean(
    config?.GW_PLUGIN_BACKEND_URL || config?.GW_PLUGIN_TEST_URL,
  );

  const mosaicNode = useSelector((store: AppStore) => getMosaicNode(store));
  const [stateAfterExpand, setStateAfterExpand] =
    React.useState<MosaicNode<string>>();

  const [stateBeforeExpand, setStateBeforeExpand] = React.useState(mosaicNode);
  const [enableUnexpand, setEnableUnexpand] = React.useState(false);

  React.useEffect(() => {
    if (enableUnexpand) {
      if (stateAfterExpand === undefined) {
        setStateAfterExpand(mosaicNode);
        return;
      }
      if (!isEqual(mosaicNode, stateAfterExpand)) {
        setEnableUnexpand(false);
        setStateAfterExpand(undefined);
      }
    }
  }, [enableUnexpand, mosaicNode, stateAfterExpand]);

  const onClose = async (): Promise<void> => {
    if (shouldPreventCloseView) {
      await confirmDialog({
        title: t('workspace-control-close-message-title'),
        description: t('workspace-control-close-message-description'),
        confirmLabel: t('workspace-control-close'),
      });
      actions.remove(path);
      removeLinkedMap(panelId);
    } else {
      actions.remove(path);
      removeLinkedMap(panelId);
    }
  };

  const expandButton =
    !enableUnexpand || isString(mosaicNode) ? (
      <CustomIconButton
        data-testid="expand-btn"
        onClick={(): void => {
          setStateBeforeExpand(mosaicNode);
          setEnableUnexpand(true);
          actions.expand(path, 70);
        }}
        sx={buttonStyle}
        tooltipTitle={t('workspace-control-expand')}
      >
        <Expand />
      </CustomIconButton>
    ) : (
      <CustomIconButton
        data-testid="unexpand-btn"
        onClick={(): void => {
          const stateBeforeExpandCopy = isString(stateBeforeExpand)
            ? stateBeforeExpand
            : { ...stateBeforeExpand };
          actions.replaceWith([], stateBeforeExpandCopy);
        }}
        sx={buttonStyle}
        tooltipTitle={t('workspace-control-unexpand')}
      >
        <UnExpand />
      </CustomIconButton>
    );

  return (
    <>
      <Box
        data-testid="workspace-view-controls-options-dropdown"
        sx={{
          [`@container workspace-view-container (min-width: 495px)`]: {
            display: 'none',
          },
          paddingLeft: '8px',
        }}
      >
        <LinkMenuConnect panelId={panelId} />
        <CustomIconButton
          aria-label="workspace view controls"
          data-testid="workspace-view-controls"
          isSelected={isOpen}
          onClick={handleClick}
          sx={{
            '&&': {
              backgroundColor: !isOpen
                ? 'transparent'
                : 'geowebColors.workspace.tabButtonHover',
              '&:hover': {
                backgroundColor: 'geowebColors.workspace.tabButtonHover',
              },
              '.MuiSvgIcon-root > path': {
                fill: isOpen
                  ? theme.palette.geowebColors.iconButtons.tool.active.color
                  : undefined,
              },
            },
          }}
          tooltipTitle={t('workspace-control-show-options')}
          variant="tool"
        >
          <ChevronDown />
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 21, horizontal: 6 }}
            onClose={() => setAnchorEl(null)}
            open={!!isOpen}
            slotProps={{
              backdrop: { style: { opacity: 0, transition: 'none' } },
            }}
            sx={{
              '& .MuiMenu-list': { padding: 0 },
              '& .MuiMenuItem-root': {
                padding: '2px',
                minHeight: '32px !important',
              },
            }}
          >
            <SplitButtonsConnect inMenuItem panelId={panelId} path={path} />
            <MenuItem>{expandButton}</MenuItem>
          </Menu>
        </CustomIconButton>
      </Box>
      <Box
        data-testid="workspace-view-controls-options-buttons"
        sx={{
          [`@container workspace-view-container (max-width: 494px)`]: {
            display: 'none',
          },
          paddingLeft: '4px',
        }}
      >
        <LinkMenuConnect panelId={panelId} />
        <SplitButtonsConnect panelId={panelId} path={path} />
        {expandButton}
      </Box>

      {hasPluginsConfigured ? (
        <OverlayPluginMenuButton panelId={panelId} />
      ) : null}

      <CustomIconButton
        data-testid="close-btn"
        id="close-window-button"
        onClick={onClose}
        sx={buttonStyle}
        tooltipTitle={t('workspace-control-close')}
      >
        <Close />
      </CustomIconButton>
    </>
  );
};

export default WorkspaceControls;
